import * as React from 'react';
import { makeStyles, tokens, ProgressBar, Text, mergeClasses } from '@fluentui/react-components';
import { PasswordStrength, evaluatePasswordStrength } from '../utils/validationUtils';

const useStyles = makeStyles({
  container: {
    marginTop: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalS,
  },
  progressBar: {
    height: '6px',
    borderRadius: tokens.borderRadiusSmall,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    marginBottom: tokens.spacingVerticalXXS,
  },
  feedback: {
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXXS,
  },
  // Cores para diferentes níveis de força
  veryWeak: {
    '& [role="progressbar"]': {
      backgroundColor: tokens.colorPaletteRedForeground1,
    },
  },
  weak: {
    '& [role="progressbar"]': {
      backgroundColor: tokens.colorPaletteRedForeground1,
    },
  },
  fair: {
    '& [role="progressbar"]': {
      backgroundColor: tokens.colorPaletteDarkOrangeForeground1,
    },
  },
  good: {
    '& [role="progressbar"]': {
      backgroundColor: tokens.colorPaletteYellowForeground1,
    },
  },
  strong: {
    '& [role="progressbar"]': {
      backgroundColor: tokens.colorPaletteGreenForeground1,
    },
  },
});

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  className
}) => {
  const styles = useStyles();
  const strengthResult = evaluatePasswordStrength(password);

  const getStrengthLabel = (strength: PasswordStrength): string => {
    switch (strength) {
      case 'very-weak':
        return 'Muito fraca';
      case 'weak':
        return 'Fraca';
      case 'fair':
        return 'Razoável';
      case 'good':
        return 'Boa';
      case 'strong':
        return 'Forte';
      default:
        return 'Desconhecida';
    }
  };

  const getStrengthColor = (strength: PasswordStrength): string => {
    switch (strength) {
      case 'very-weak':
      case 'weak':
        return styles.weak;
      case 'fair':
        return styles.fair;
      case 'good':
        return styles.good;
      case 'strong':
        return styles.strong;
      default:
        return styles.veryWeak;
    }
  };

  // Não mostrar nada se a senha estiver vazia
  if (!password) {
    return null;
  }

  return (
    <div className={mergeClasses(styles.container, className)}>
      <Text className={styles.label}>
        Força da senha: {getStrengthLabel(strengthResult.strength)}
      </Text>

      <ProgressBar
        value={strengthResult.score / 100}
        className={mergeClasses(styles.progressBar, getStrengthColor(strengthResult.strength))}
        aria-label={`Força da senha: ${getStrengthLabel(strengthResult.strength)}`}
      />

      {strengthResult.feedback.length > 0 && (
        <div className={styles.feedback}>
          {strengthResult.feedback.map((item, index) => (
            <Text key={index} style={{ color: tokens.colorPaletteRedForeground1 }}>
              • {item}
            </Text>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;